import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '../../../components/ui/Card';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Button } from '../../../components/ui/Button';
import { useAuth } from '../../../hooks/useAuth';
import { useLabel } from '../../../hooks/useLabel';
import { LabelData } from '../../../types/label';
import { Loader } from '../../../components/ui/Loader';
import { Toggle } from '../../../components/Toggle';
import { ShippingLabel } from '../../../components/shippingLabel';

// Sample data for the shipping label
const sampleShipTo = {
  name: 'Mr Avinash',
  address: '105 Test Society New test compound test Nagar',
  city: 'Andheri West',
  postalCode: '11111',
};

const samplePickupAddress = {
  name: 'Mr Avinash',
  address: '105 Test Society New test compound test Nagar,Andheri West',
  city: 'Mumbai',
  postalCode: '11111',
  contact: '99999999',
  email: 'test@gmail.com',
};

const sampleItems = [
  {
    sku: 'N/A',
    product: 'T-Shirt',
    qty: 1,
    amount: 799,
  },
];

const ManageLabel: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading, fetchSettings, saveSettings, uploadLogo } = useLabel();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [labelData, setLabelData] = useState<LabelData>({
    showLogo: false,
    logoText: 'Parcelpicks',
    instagramQR: false,
    instagramUrl: 'https://www.instagram.com/parcelpicks',
    showParcelpicksBranding: false,
    showYourBranding: false,

    customerNumber: true,
    customerName: true,
    customerEmail: true,
    paymentMode: true,
    customerAddress: true,
    routingCode: true,
    showDimensions: true,
    showWeight: true,

    pickupNumber: true,
    pickupName: true,
    pickupEmail: true,
    pickupPaymentMode: true,
    pickupAddress: true,

    product: true,
    sku: true,
    qty: true,
    amount: true,

    note: 'Please do not accept the shipment if the package appears tampered.',

    showOrderNumber: true,
    showItemsTable: true,
    showCourierSection: true,
    showPaymentSection: true,
    showQRCode: true,
    showSystemFooter: true,
    logoFile: null,
    logoPreview: '/icons/default-logo.png',
    labelSize: 'a4',
    labelType: 'standard',
  });

  React.useEffect(() => {
    if (user?.id) {
      fetchSettings(user.id).then((data) => {
        if (data) {
          setLabelData((prev) => ({ ...prev, ...data }));
        }
      });
    }
  }, [user?.id, fetchSettings]);

  const handleCheckboxChange = (field: keyof LabelData) => {
    setLabelData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleToggleChange = (field: keyof LabelData, value: boolean) => {
    setLabelData((prev) => {
      const newState = { ...prev, [field]: value };

      // Mutual exclusion for branding
      if (field === 'showParcelpicksBranding' && value) {
        newState.showYourBranding = false;
      } else if (field === 'showYourBranding' && value) {
        newState.showParcelpicksBranding = false;
      }

      return newState;
    });
  };

  const handleTextChange = (field: keyof LabelData, value: string) => {
    setLabelData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePrinterChange = (size: 'a4' | 'a6') => {
    setLabelData((prev) => ({
      ...prev,
      labelSize: size,
      labelType: size === 'a4' ? 'standard' : 'thermal',
    }));
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file && user?.id) {
      const allowedMimeTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const isAllowedExt = ['png', 'jpg', 'jpeg'].includes(fileExt || '');

      if (!allowedMimeTypes.includes(file.type) && !isAllowedExt) {
        toast.error('Only PNG and JPG/JPEG formats are allowed for logo.');
        return;
      }

      if (file.size > 200 * 1024) {
        toast.error('Image size should be upto 200kb');
        return;
      }

      const logoUrl = await uploadLogo(file, user.id);
      if (logoUrl) {
        setLabelData((prev) => ({
          ...prev,
          logoFile: file,
          logoPreview: logoUrl,
        }));
      }
    }
  };

  const handleSubmit = async () => {
    if (user?.id) {
      await saveSettings(user.id, labelData);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 lg:p-8 relative">
      {loading && (
        <div className="fixed inset-0 bg-white/50 z-50 flex items-center justify-center">
          <Loader text="Processing..." />
        </div>
      )}
      <div className="mx-auto">
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Manage Label</h1>
          </div>
          <div className="flex items-center gap-4 text-gray-600">
            <span className="font-medium">
              {user?.firstName} {user?.lastName}, {user?.email}
            </span>
            <Button variant="link" className="text-primary p-0 h-auto">
              Go to profile
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Settings */}
          <div className="space-y-8">
            {/* Common Settings Card */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-6">
                Select Printer Type
              </h2>

              <div className=" flex flex-col lg:flex-row items-center gap-4">
                {/* Standard Desktop Printers */}
                <div
                  className="flex items-start space-x-4  py-2 px-4 border-2 rounded-lg cursor-pointer transition-colors"
                  style={{
                    borderColor:
                      labelData.labelSize === 'a4' ? '#0891b2' : '#e5e7eb',
                    backgroundColor:
                      labelData.labelSize === 'a4' ? '#f0f9fa' : 'white',
                  }}
                  onClick={() => handlePrinterChange('a4')}
                >
                  <div className="mt-1">
                    <input
                      type="radio"
                      id="standard"
                      name="printer"
                      checked={labelData.labelSize === 'a4'}
                      onChange={() => handlePrinterChange('a4')}
                      className="w-5 h-5 cursor-pointer"
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="standard"
                      className="font-semibold text-gray-800 cursor-pointer"
                    >
                      Size A4
                    </label>
                  </div>
                </div>

                {/* Thermal Label Printers */}
                <div
                  className="flex items-start space-x-4 py-2 px-4 border rounded-lg cursor-pointer transition-colors"
                  style={{
                    borderColor:
                      labelData.labelSize === 'a6' ? '#0891b2' : '#e5e7eb',
                    backgroundColor:
                      labelData.labelSize === 'a6' ? '#f0f9fa' : 'white',
                  }}
                  onClick={() => handlePrinterChange('a6')}
                >
                  <div className="mt-1">
                    <input
                      type="radio"
                      id="thermal"
                      name="printer"
                      checked={labelData.labelSize === 'a6'}
                      onChange={() => handlePrinterChange('a6')}
                      className="w-5 h-5 cursor-pointer"
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="thermal"
                      className="font-semibold text-gray-800 cursor-pointer"
                    >
                      Thermal label
                    </label>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              {/* Branding Section */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800">
                  Branding
                </h2>
                <p className="text-xs text-gray-500 mb-4">
                  (Choose how your branding appears on label)
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-8">
                    <div className="flex-1 space-y-4">
                      <div className="flex flex-col gap-5">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={labelData.showLogo}
                            onChange={() => handleCheckboxChange('showLogo')}
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Show logo on label
                          </span>
                        </div>

                        {labelData.showLogo && (
                          <div className="ml-8 flex flex-col gap-3">
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileUpload}
                              accept="image/png, image/jpeg, image/jpg, .png, .jpg, .jpeg"
                              className="hidden"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              className="w-fit h-8 px-4"
                            >
                              Upload
                            </Button>

                            {labelData.logoPreview && (
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-gray-500 truncate max-w-[150px]">
                                  Logo selected
                                </span>
                                <button
                                  onClick={() =>
                                    setLabelData((p) => ({
                                      ...p,
                                      logoPreview: '',
                                    }))
                                  }
                                  className="text-[10px] text-red-500 hover:underline"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={labelData.instagramQR}
                              onChange={() =>
                                handleCheckboxChange('instagramQR')
                              }
                            />
                            <span className="text-sm font-medium">
                              Show Insta QR on label
                            </span>
                          </div>
                          <input
                            type="text"
                            value={labelData.instagramUrl}
                            onChange={(e) =>
                              handleTextChange('instagramUrl', e.target.value)
                            }
                            className="w-1/2 px-3 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary text-sm"
                            placeholder="Instagram URL"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium ">
                          Show Parcelpicks branding on label
                        </span>
                        <Toggle
                          checked={labelData.showParcelpicksBranding}
                          onChange={(val) =>
                            handleToggleChange('showParcelpicksBranding', val)
                          }
                        />
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium ">
                            Show Your branding on label
                          </span>
                          <Toggle
                            checked={labelData.showYourBranding}
                            onChange={(val) =>
                              handleToggleChange('showYourBranding', val)
                            }
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-medium text-gray-600">
                            Enter your brand name
                          </label>
                          <input
                            type="text"
                            value={labelData.logoText}
                            onChange={(e) =>
                              handleTextChange('logoText', e.target.value)
                            }
                            className="w-full px-3 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary text-sm disabled:bg-gray-50 disabled:text-gray-400"
                            placeholder="Enter your brand name"
                            maxLength={10}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <hr className="my-6" />

              {/* Delivery Details Section */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800">
                  Delivery Details
                </h2>
                <p className="text-xs text-gray-500 mb-4">
                  (Hide what delivery info to show on the label)
                </p>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-y-4 gap-x-8">
                  {[
                    { id: 'customerNumber', label: 'Customer Number' },
                    { id: 'customerName', label: 'Customer Name' },
                    { id: 'customerEmail', label: 'Customer Email' },
                    { id: 'paymentMode', label: 'Payment Mode' },
                    { id: 'customerAddress', label: 'Customer Address' },
                    { id: 'routingCode', label: 'Routing Code' },
                    { id: 'showDimensions', label: 'Dimensions' },
                    { id: 'showWeight', label: 'Weight' },
                  ].map((item) => (
                    <div key={item.id} className="flex items-center gap-3">
                      <Checkbox
                        checked={
                          labelData[item.id as keyof LabelData] as boolean
                        }
                        onChange={() =>
                          handleCheckboxChange(item.id as keyof LabelData)
                        }
                      />
                      <span className="text-sm text-gray-700">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="my-6" />

              {/* Pickup Details Section */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800">
                  Pickup Details
                </h2>
                <p className="text-xs text-gray-500 mb-4">
                  Hide relevant pickup and RTO information on the label
                </p>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-y-4 gap-x-8">
                  {[
                    { id: 'pickupNumber', label: 'Pickup Contact' },
                    { id: 'pickupName', label: 'Pickup Name' },
                    { id: 'pickupEmail', label: 'Pickup Email' },
                    { id: 'pickupAddress', label: 'Pickup Address' },
                  ].map((item) => (
                    <div key={item.id} className="flex items-center gap-3">
                      <Checkbox
                        checked={
                          labelData[item.id as keyof LabelData] as boolean
                        }
                        onChange={() =>
                          handleCheckboxChange(item.id as keyof LabelData)
                        }
                      />
                      <span className="text-sm text-gray-700">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="my-6" />

              {/* Order Details Section */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800">
                  Order Details
                </h2>
                <p className="text-xs text-gray-500 mb-4">
                  (Choose the order and product details to hide)
                </p>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-y-4 gap-x-8">
                  {[
                    { id: 'product', label: 'Product' },
                    { id: 'sku', label: 'SKU' },
                    { id: 'qty', label: 'QTY' },
                    { id: 'amount', label: 'Amount' },
                  ].map((item) => (
                    <div key={item.id} className="flex items-center gap-3">
                      <Checkbox
                        checked={
                          labelData[item.id as keyof LabelData] as boolean
                        }
                        onChange={() =>
                          handleCheckboxChange(item.id as keyof LabelData)
                        }
                      />
                      <span className="text-sm text-gray-700">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="my-6" />

              {/* Note Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Note
                  </h2>
                  <span className={`text-xs font-semibold ${((labelData.note || '').length >= 120) ? 'text-amber-600' : 'text-gray-500'}`}>
                    {(labelData.note || '').length} / 120 characters
                  </span>
                </div>
                <textarea
                  value={labelData.note}
                  onChange={(e) => handleTextChange('note', e.target.value.slice(0, 120))}
                  maxLength={120}
                  placeholder="Please do not accept the shipment if the package appears tampered."
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary text-sm min-h-[80px]"
                />
              </div>
            </Card>

            {/* Submit Button */}
            <Button onClick={handleSubmit} className="w-full py-3 text-lg">
              Submit
            </Button>
          </div>

          {/* Right Panel - Label Preview */}
          <div className="lg:sticky lg:top-8">
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">
                Label Preview
              </h2>

              <div className="mt-6 border-t pt-6">
                <ShippingLabel
                  shipTo={sampleShipTo}
                  customerNumber="99999999"
                  orderNumber="AWB909098980"
                  paymentType="Prepaid"
                  collectedAmount={799}
                  items={sampleItems}
                  pickupAddress={samplePickupAddress}
                  trackingNumber="AWB909098980"
                  // New Props mapping
                  showLogo={labelData.showLogo}
                  logoText={labelData.logoText}
                  instagramQR={labelData.instagramQR}
                  instagramUrl={labelData.instagramUrl}
                  isThermal={labelData.labelSize === 'a6'}
                  showParcelpicksBranding={labelData.showParcelpicksBranding}
                  showYourBranding={labelData.showYourBranding}
                  customerName={labelData.customerName}
                  customerNumberVisible={labelData.customerNumber}
                  customerEmail={labelData.customerEmail}
                  paymentMode={labelData.paymentMode}
                  customerAddress={labelData.customerAddress}
                  routingCode={labelData.routingCode}
                  showDimensions={labelData.showDimensions}
                  showWeight={labelData.showWeight}
                  product={labelData.product}
                  sku={labelData.sku}
                  qty={labelData.qty}
                  amount={labelData.amount}
                  pickupNameVisible={labelData.pickupName}
                  pickupAddressVisible={labelData.pickupAddress}
                  pickupContactVisible={labelData.pickupNumber}
                  pickupEmailVisible={labelData.pickupEmail}
                  note={!!labelData.note}
                  noteText={labelData.note}
                  showOrderNumber={labelData.showOrderNumber}
                  showCourierSection={labelData.showCourierSection}
                  showPaymentSection={labelData.showPaymentSection}
                  showQRCode={labelData.showQRCode}
                  showSystemFooter={labelData.showSystemFooter}
                  logoUrl={labelData.logoPreview}
                  weight={0.5}
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageLabel;
